import { FC } from "react"

import { ReactComponent as IconDocument } from "@common_assets/svg/document-outlined.svg"
import { ReactComponent as IconTrash } from "@common_assets/svg/trash.svg"
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material"
import IconWrapper from "components/common/icon/iconWrapper"
import { useTranslation } from "react-i18next"
import { ExportTask } from "utils/exportTasks"

interface IExportFileRowProps {
  task: ExportTask
  onClick: () => void
  onRemove?: () => void
  px: number
  outlineOffset: number
}

/**
 * One generated report in an export list: click downloads it; with `onRemove`, a trash
 * button next to it removes it from the list.
 */
const ExportFileRow: FC<IExportFileRowProps> = ({
  task,
  onClick,
  onRemove,
  px,
  outlineOffset,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        "&:hover": { backgroundColor: theme.palette.action.hover },
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-label={`${t("allTransactions.downloadFile")}: ${task.filename}`}
        onClick={onClick}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            onClick()
          }
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          py: 1.5,
          px,
          pr: onRemove ? 0.5 : px,
          cursor: "pointer",
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset,
          },
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <IconWrapper
            sx={{
              width: "20px",
              height: "20px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconDocument
              style={{
                width: "20px",
                height: "20px",
                color: theme.palette.primary.main,
              }}
            />
          </IconWrapper>
          <Typography
            variant="body2"
            sx={{
              fontWeight: theme.typography.fontWeightRegular,
              minWidth: 0,
              overflowWrap: "anywhere",
            }}
          >
            {task.filename}
          </Typography>
        </Stack>
      </Box>
      {onRemove && (
        <Tooltip title={t("export.removeFromList")} arrow placement="top">
          <IconButton
            size="small"
            aria-label={`${t("export.removeFromList")}: ${task.filename}`}
            onClick={onRemove}
            sx={{ mr: 1, flexShrink: 0, color: "primary.main" }}
          >
            <IconTrash width="20px" height="20px" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  )
}

export default ExportFileRow
